import { useState, useEffect, useRef } from "react";
import { ArrowRight, Youtube, LogOut, Twitter, Facebook, ChevronDown, ChevronUp } from "lucide-react";
import Wordmark from "../components/Wordmark";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";
const FREE_LIMIT = 2;

function getStoredUser() {
  const token = localStorage.getItem("yousift_token");
  const user_id = localStorage.getItem("yousift_user_id");
  const email = localStorage.getItem("yousift_email");
  if (token && user_id && email) return { token, user_id, email };
  return null;
}

export default function Dashboard() {
  const [user] = useState(() => getStoredUser());
  const [sifts, setSifts] = useState([]);
  const [siftCount, setSiftCount] = useState(0);
  const [isPremium, setIsPremium] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [sifting, setSifting] = useState(false);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const newSiftRef = useRef(null);

  useEffect(() => {
    if (!user) { window.location.href = "/"; return; }
    const pending = sessionStorage.getItem("yousift_pending_url");
    if (pending) {
      sessionStorage.removeItem("yousift_pending_url");
      setUrl(pending);
    }
    loadUserInfo();
    loadSifts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${user.token}`,
  });

  const loadUserInfo = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/me`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSiftCount(data.sift_count || 0);
        setIsPremium(data.is_premium || false);
      } else {
        // Token invalid
        handleLogout();
      }
    } catch {}
  };

  const loadSifts = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/user/sifts`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSifts(data.sifts || []);
      }
    } catch {} finally {
      setLoadingHistory(false);
    }
  };

  // Progress bar animation
  useEffect(() => {
    if (!sifting) { setProgress(0); return; }
    setProgress(20);
    const interval = setInterval(() => {
      setProgress(p => { if (p >= 85) { clearInterval(interval); return 85; } return p + (85 - p) * 0.08; });
    }, 400);
    return () => clearInterval(interval);
  }, [sifting]);

  const handleLogout = () => {
    localStorage.removeItem("yousift_token");
    localStorage.removeItem("yousift_user_id");
    localStorage.removeItem("yousift_email");
    window.location.href = "/";
  };

  const handleSift = async (e) => {
    e.preventDefault();
    if (!url.trim() || sifting) return;
    if (!isPremium && siftCount >= FREE_LIMIT) { setShowUpgrade(true); return; }

    setSifting(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/api/sift`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ youtube_url: url.trim(), user_id: user.user_id }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (res.status === 402) setShowUpgrade(true);
        else setError(data.detail || "Something went wrong. Please try again.");
        setSifting(false);
        return;
      }

      setProgress(100);
      setTimeout(() => {
        setSifting(false);
        const newSift = { ...data, created_at: new Date().toISOString() };
        const newCount = data.sift_count || siftCount + 1;
        setSifts(prev => [newSift, ...prev]);
        setSiftCount(newCount);
        setUrl("");
        setTimeout(() => newSiftRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
        // Auto-show upgrade modal if they've now hit the limit
        if (!isPremium && newCount >= FREE_LIMIT) {
          setTimeout(() => setShowUpgrade(true), 1200);
        }
      }, 600);
    } catch {
      setError("We couldn't reach the server. Please try again.");
      setSifting(false);
    }
  };

  const remaining = isPremium ? null : Math.max(0, FREE_LIMIT - siftCount);
  const limitReached = !isPremium && siftCount >= FREE_LIMIT;

  return (
    <div className="min-h-screen bg-[#0F0F0F] flex flex-col">
      {/* Top loading bar */}
      {sifting && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 9999, height: "4px", background: "#1A1A1A" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: "#FF0000", transition: "width 0.3s ease-out" }} />
        </div>
      )}

      {showUpgrade && <UpgradeModal onDismiss={() => setShowUpgrade(false)} />}

      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4 border-b border-[#1A1A1A]">
        <a href="/"><Wordmark size="sm" darkBg={true} /></a>
        <div className="flex items-center gap-4">
          <span className="text-gray-500 text-sm hidden sm:block">{user?.email}</span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-gray-400 hover:text-white text-sm transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </nav>

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-12">
        {/* Header + counter */}
        <div className="mb-10">
          <h1 className="text-3xl font-black text-white mb-1">Your Dashboard</h1>
          {isPremium ? (
            <p className="text-gray-500 text-sm">Unlimited sifts · Premium</p>
          ) : (
            <p className="text-gray-500 text-sm">
              {remaining === 0
                ? "You've used all your free sifts."
                : `${remaining} free sift${remaining === 1 ? "" : "s"} remaining`}
            </p>
          )}
        </div>

        {/* Usage bar */}
        {!isPremium && (
          <div className="mb-8 bg-[#141414] border border-[#2A2A2A] rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Sifts used</span>
              <span className="text-sm font-bold text-white">{siftCount} / {FREE_LIMIT}</span>
            </div>
            <div className="h-2 bg-[#1A1A1A] rounded-full overflow-hidden mb-4">
              <div
                className="h-full bg-[#FF0000] rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, (siftCount / FREE_LIMIT) * 100)}%` }}
              />
            </div>
            {limitReached ? (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-400">Upgrade for unlimited sifts, tweets, and Facebook posts.</p>
                <a href="/pricing" className="shrink-0 ml-4 px-4 py-2 bg-[#FF0000] hover:bg-[#CC0000] text-white font-black text-sm rounded-xl transition-all">
                  Upgrade
                </a>
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                Free plan includes {FREE_LIMIT} sifts.{" "}
                <a href="/pricing" className="text-[#FF0000] hover:underline">Upgrade for unlimited.</a>
              </p>
            )}
          </div>
        )}

        {/* New sift form */}
        {!limitReached && (
          <form onSubmit={handleSift} className="mb-12">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Youtube size={18} className="text-gray-500" />
                </div>
                <input
                  type="url"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="Paste a YouTube URL to sift..."
                  disabled={sifting}
                  className="w-full pl-11 pr-4 py-4 bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-[#FF0000] transition-all disabled:opacity-50"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={sifting || !url.trim()}
                className="px-6 py-4 bg-[#FF0000] hover:bg-[#CC0000] disabled:opacity-50 disabled:cursor-not-allowed text-white font-black rounded-xl transition-all flex items-center gap-2"
              >
                {sifting ? <Spinner /> : <><span>Sift</span><ArrowRight size={18} /></>}
              </button>
            </div>
            {error && (
              <div className="mt-3 bg-red-950 border border-red-800 text-red-200 rounded-xl px-4 py-3 text-sm">
                {error}
              </div>
            )}
          </form>
        )}

        {/* Sift history */}
        <div ref={newSiftRef}>
          {loadingHistory ? (
            <div className="flex items-center justify-center py-16 text-gray-600">
              <Spinner /> <span className="ml-2 text-sm">Loading your sifts...</span>
            </div>
          ) : sifts.length === 0 ? (
            <div className="text-center py-16 text-gray-600">
              <Youtube size={40} className="mx-auto mb-4 opacity-30" />
              <p className="text-sm">No sifts yet. Paste a YouTube URL above to get started.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">Your Sifts</h2>
              {sifts.map(sift => (
                <SiftCard key={sift.sift_id} sift={sift} user={user} />
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="py-6 text-center border-t border-[#1A1A1A]">
        <p className="text-gray-700 text-xs">
          &copy; {new Date().getFullYear()}{" "}
          <span className="text-gray-500 font-semibold">Bethany Labs</span> · All rights reserved
        </p>
      </footer>
    </div>
  );
}

function SiftCard({ sift, user }) {
  const [expanded, setExpanded] = useState(false);
  const [socialTab, setSocialTab] = useState("tweets");
  const [tweets, setTweets] = useState(sift.tweets || null);
  const [fbPost, setFbPost] = useState(sift.facebook_post || null);
  const [imageOptions, setImageOptions] = useState(null);
  const [socialLoading, setSocialLoading] = useState(false);

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${user.token}`,
  });

  const loadSocial = async (type) => {
    if (type === "tweets" && tweets) return;
    if (type === "facebook" && fbPost) return;
    setSocialLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/generate-social`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ sift_id: sift.sift_id, type }),
      });
      if (res.ok) {
        const data = await res.json();
        if (type === "tweets") setTweets(data.tweets || []);
        if (type === "facebook") {
          setFbPost(data.facebook_post || "");
          setImageOptions(data.image_options || []);
        }
      }
    } catch {} finally {
      setSocialLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setSocialTab(tab);
    loadSocial(tab);
  };

  const handleExpand = () => {
    const opening = !expanded;
    setExpanded(opening);
    if (opening) loadSocial("tweets");
  };

  const date = sift.created_at
    ? new Date(sift.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : "";

  return (
    <div className="bg-[#141414] border border-[#2A2A2A] rounded-2xl overflow-hidden">
      {/* Card header */}
      <button
        onClick={handleExpand}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-[#1A1A1A] transition-colors"
      >
        {sift.thumbnail_url && (
          <img
            src={sift.thumbnail_url}
            alt=""
            className="w-20 h-14 object-cover rounded-lg shrink-0 bg-[#1A1A1A]"
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm truncate">{sift.video_title || "YouTube Video"}</p>
          {date && <p className="text-gray-600 text-xs mt-0.5">{date}</p>}
        </div>
        <div className="text-gray-500 shrink-0">
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-[#2A2A2A] p-4 space-y-6">
          {/* LinkedIn post */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">LinkedIn Post</p>
            <div className="bg-[#1A1A1A] rounded-xl p-4 text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
              {sift.linkedin_post}
            </div>
          </div>

          {/* Social content tabs */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Social Content</p>
            <div className="flex bg-[#1A1A1A] rounded-xl p-1 mb-4 gap-1">
              {[
                { id: "tweets", label: "Tweets / X", Icon: Twitter },
                { id: "facebook", label: "Facebook", Icon: Facebook },
              ].map(({ id, label, Icon }) => (
                <button
                  key={id}
                  onClick={() => handleTabChange(id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg transition-all ${
                    socialTab === id ? "bg-[#FF0000] text-white" : "text-gray-400 hover:text-white"
                  }`}
                >
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>

            {socialLoading ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                <Spinner />
                <span className="ml-2 text-sm">Generating...</span>
              </div>
            ) : socialTab === "tweets" ? (
              <div className="space-y-2">
                {tweets ? tweets.map((tweet, i) => (
                  <TweetCard key={i} index={i + 1} text={tweet} />
                )) : (
                  <p className="text-gray-600 text-sm text-center py-4">Loading tweets...</p>
                )}
              </div>
            ) : (
              <div>
                {fbPost ? (
                  <>
                    <div className="bg-[#1A1A1A] rounded-xl p-4 text-gray-300 text-sm leading-relaxed whitespace-pre-wrap mb-4">
                      {fbPost}
                    </div>
                    {imageOptions && imageOptions.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Image options</p>
                        <div className="flex gap-4 flex-wrap">
                          {imageOptions.map((opt, i) => (
                            <div key={i} className="text-center">
                              {opt.type === "thumbnail" ? (
                                <img
                                  src={opt.value}
                                  alt={opt.label}
                                  className="w-32 h-20 object-cover rounded-lg"
                                />
                              ) : (
                                <div
                                  className="w-32 h-20 rounded-lg border border-[#2A2A2A]"
                                  style={{ background: opt.value }}
                                />
                              )}
                              <p className="text-gray-500 text-xs mt-1">{opt.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-gray-600 text-sm text-center py-4">Loading Facebook post...</p>
                )}
              </div>
            )}
          </div>

          {/* Blog post */}
          {sift.blog_post && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">Blog Article</p>
              <div className="bg-[#1A1A1A] rounded-xl p-4 text-gray-300 text-sm leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto">
                {sift.blog_post}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TweetCard({ index, text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-4 flex items-start gap-3">
      <span className="text-[#FF0000] font-black text-xs w-5 shrink-0 pt-0.5">{index}</span>
      <p className="text-gray-300 text-sm leading-relaxed flex-1">{text}</p>
      <button
        onClick={handleCopy}
        className="text-gray-600 hover:text-gray-300 text-xs shrink-0 transition-colors whitespace-nowrap"
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

function UpgradeModal({ onDismiss }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-[#141414] border border-[#2A2A2A] rounded-2xl p-8 shadow-2xl text-center">
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-400 text-lg leading-none"
        >
          ✕
        </button>
        <div className="w-12 h-12 bg-[#FF0000]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
          <span className="text-[#FF0000] text-2xl font-black">∞</span>
        </div>
        <h2 className="text-xl font-black text-white mb-2">You've used your free sifts</h2>
        <p className="text-gray-400 text-sm mb-6 leading-relaxed">
          Upgrade to get unlimited sifts, all social formats, and priority processing.
        </p>
        <a
          href="/pricing"
          className="block w-full py-4 bg-[#FF0000] hover:bg-[#CC0000] text-white font-black rounded-xl transition-all"
        >
          See Plans
        </a>
        <button
          onClick={onDismiss}
          className="mt-3 text-gray-600 hover:text-gray-400 text-sm w-full transition-colors"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
}
